import {
	Box,
	Divider,
	FormControlLabel,
	FormHelperText,
	Grid,
	Radio,
	RadioGroup,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useController,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
	useState,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import {
	API_DATE_FORMAT,
	DISPLAY_DATE_FORMAT,
	notFutureDate,
} from '@/utils/dateHelper';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';

interface Props {
	type: FormType;
	details?: any;
	allEnumList?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditRecoveryAssessmentScaleForm = forwardRef(
	({ type, setType, details, allEnumList }: Props, ref) => {
		const { t } = useTranslation();

		const [answerList, setAnswerList] = useState(() =>
			Array.from({ length: 24 }, (_, i) => {
				const id = i + 1;
				return {
					id,
					title: t(`preAdmission.recoveryAssessmentTitle${id}`) + '*',
					answer1: false,
					answer2: false,
					answer3: false,
					answer4: false,
					answer5: false,
				};
			})
		);

		const defaultValues: DefaultValues<any> = {};

		useEffect(() => {
			if (details) {
				methods.reset(details);

				if (details.answerList) {
					const restoredList = details.answerList?.map(
						(score: number, index: number) => {
							const id = index + 1;
							const title =
								t(`preAdmission.recoveryAssessmentTitle${id}`) + '*';
							const row: any = {
								id,
								title,
								answer1: false,
								answer2: false,
								answer3: false,
								answer4: false,
								answer5: false,
							};

							row[`answer${score}`] = true;
							return row;
						}
					);
					setAnswerList(restoredList);
					methods.setValue('answerList', restoredList);
					console.log(restoredList);
				}
			}
		}, [details]);

		const formSchema = Yup.object().shape({
			fillingDate: Yup.string()
				.required(t('errorMessage.pleaseSelect'))
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
			answerList: Yup.array()
				.of(
					Yup.object().test(
						'at-least-one-answer',
						t('errorMessage.pleaseSelect'), // 错误提示
						(row) => {
							if (!row) return false;
							const { answer1, answer2, answer3, answer4, answer5 } =
								row as any;
							return [answer1, answer2, answer3, answer4, answer5].some(
								(val) => val === true
							);
						}
					)
				)
				.required(t('errorMessage.pleaseSelect'))
				.test(
					'all-rows-answered',
					t('errorMessage.pleaseAnswerAllQuestions'),
					(list) => {
						if (!Array.isArray(list)) return false;
						return list.length === 24; // 可选：确保数量正确
					}
				),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const answerListField = useController({
			name: 'answerList',
			control: methods.control,
		});

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.setValue('answerList', answerList);
				console.log(JSON.stringify(methods.getValues('answerList')));
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		const radioRenderCell = (params: any, score: number) => {
			const { id, row, api } = params;

			const isChecked = row[`answer${score}`] === true;

			const handleChange = () => {
				const updates: Record<string, boolean> = {
					answer1: false,
					answer2: false,
					answer3: false,
					answer4: false,
					answer5: false,
				};
				updates[`answer${score}`] = true;

				api.updateRows([{ id, ...updates }]);
				const list = api.getAllRowIds().map((id: any) => api.getRow(id));
				setAnswerList(list);

				methods.setValue('answerList', list);
				methods.trigger();
			};
			return (
				<Radio checked={isChecked} value={score} onChange={handleChange} />
			);
		};

		const columns: GridColDef[] = [
			{
				field: 'title',
				headerName: '',
				flex: 30,
			},
			{
				field: 'answer1',
				headerName: t('preAdmission.score1'),
				flex: 10,
				renderCell: (params: any) => radioRenderCell(params, 1),
			},
			{
				field: 'answer2',
				headerName: t('preAdmission.score2'),
				flex: 10,
				renderCell: (params: any) => radioRenderCell(params, 2),
			},
			{
				field: 'answer3',
				headerName: t('preAdmission.score3'),
				flex: 10,
				renderCell: (params: any) => radioRenderCell(params, 3),
			},
			{
				field: 'answer4',
				headerName: t('preAdmission.score4'),
				flex: 10,
				renderCell: (params: any) => radioRenderCell(params, 4),
			},
			{
				field: 'answer5',
				headerName: t('preAdmission.score5'),
				flex: 10,
				renderCell: (params: any) => radioRenderCell(params, 5),
			},
		];
		return (
			<>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.en_name_en')}
									value={details?.nameEn}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_name_en')}
									value={details?.nameZh}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.birthday')}
									value={
										details?.dateOfBirth
											? dayjs(details?.dateOfBirth)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_fileNo')}
									value={details?.fileNo}
								/>
							</Grid>

							<Grid size={6}>
								<AppDatePicker
									control={methods.control}
									name='fillingDate'
									label={t('preAdmission.fillingDate') + '*'}
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.idNumber')}
									value={details?.hkid}
								/>
							</Grid>
						</Grid>

						<Divider sx={{ margin: '24px 0' }} />

						<Typography
							variant='h6'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('preAdmission.recoveryAssessmentQuestionnaire')}
						</Typography>
						<Typography
							variant='body2'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('preAdmission.wbsTips')}
						</Typography>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12} display='flex'>
								<LocalDataGrid
									rows={answerList}
									columns={columns}
									getRowId={(row) => row.id}
									pagination={false}
								/>
							</Grid>
						</Grid>
					</form>
					<FormHelperText color='error' style={{ color: '#d32f2f' }}>
						{answerListField.fieldState.error
							? t('errorMessage.pleaseSelect')
							: ''}
					</FormHelperText>
				</FormProvider>
			</>
		);
	}
);

EditRecoveryAssessmentScaleForm.displayName = 'EditRecoveryAssessmentScaleForm';

export default EditRecoveryAssessmentScaleForm;
